import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Book, ArrowLeft, BookOpen, Clock, User } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';

const PublicStory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { fetchStory(); }, [id]);

  const fetchStory = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/story/${id}`);
      if (res.ok) setStory(await res.json());
      else setNotFound(true);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-600 mb-2">Story Not Found</h2>
        <p className="text-gray-500 mb-6">This story may be private or doesn't exist.</p>
        <Button onClick={() => navigate('/')} className="btn-primary text-white rounded-full border-0 px-8">Go Home</Button>
      </div>
    </div>
  );

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50" data-testid="public-story-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <Book className="w-8 h-8 text-purple-500" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">Life Echo</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button variant="outline" onClick={() => navigate('/community')} className="rounded-full" data-testid="back-community-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Community
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Story Header */}
        <div className="glass-effect rounded-2xl p-8 mb-8 text-center">
          <BookOpen className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2" data-testid="story-title">{story.title}</h2>
          <p className="text-lg text-gray-500 italic mb-4">{story.subtitle}</p>
          <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1"><User className="w-4 h-4" /> {story.user_name}</span>
            <span>&middot;</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {formatDate(story.created_at)}</span>
            <span>&middot;</span>
            <span>{story.word_count?.toLocaleString()} words</span>
          </div>
        </div>

        {/* Story Content */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="max-w-3xl mx-auto prose prose-lg">
            <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
              {story.content?.split('\n').map((line, i) => {
                if (line.startsWith('###')) return <h3 key={i} className="text-2xl font-bold text-gray-800 mt-8 mb-4">{line.replace('###', '').trim()}</h3>;
                if (line.startsWith('##')) return <h2 key={i} className="text-3xl font-bold text-gray-800 mt-6 mb-3 text-center italic">{line.replace('##', '').trim()}</h2>;
                if (line.startsWith('#')) return <h1 key={i} className="text-4xl font-bold text-gray-800 mb-2 text-center">{line.replace('#', '').trim()}</h1>;
                if (line.trim() === '---') return <hr key={i} className="my-8 border-gray-300" />;
                if (line.trim()) return <p key={i} className="mb-4 text-justify">{line}</p>;
                return <br key={i} />;
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center glass-effect rounded-2xl p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-2">Want to create your own autobiography?</h3>
          <p className="text-gray-500 mb-4">Answer a few questions and let AI write your story.</p>
          <Button onClick={() => navigate('/')} className="btn-primary text-white rounded-full border-0 px-10 py-5" data-testid="create-own-btn">
            Start My Story
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublicStory;
